import React from 'react';
import { BOOKING_URL } from '../../../constants/config';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider, // Added for visual separation
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; // For Accordion
import HealingIcon from '@mui/icons-material/Healing'; // Example for candidate section
import StarIcon from '@mui/icons-material/Star'; // Example for types of crowns
import SpaIcon from '@mui/icons-material/Spa'; // Example for caring for crowns
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const DentalCrowns = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const benefits = [
    'Restores tooth strength and chewing function effectively',
    'Protects weak, cracked, or worn-down teeth from further damage',
    'Dramatically improves tooth appearance (shape, size, color, and alignment)',
    'Provides essential support for dental bridges',
    'Securely covers and completes dental implants',
    'Offers a durable, long-lasting solution for a confident smile'
  ];

  const process = [
    {
      title: '1. Comprehensive Consultation & Smile Assessment',
      description: 'We begin with a thorough examination, including digital X-rays or 3D scans if necessary. We discuss your goals, concerns, and determine if a crown is the best solution for you.'
    },
    {
      title: '2. Tooth Preparation & Impression',
      description: 'The tooth is gently reshaped under local anesthesia to ensure a comfortable experience and create the ideal fit for the crown. We then take a precise digital or traditional impression.'
    },
    {
      title: '3. Custom Temporary Crown',
      description: 'While your permanent crown is meticulously crafted by our expert dental lab, a high-quality temporary crown is placed to protect your tooth and maintain aesthetics.'
    },
    {
      title: '4. Final Crown Fitting & Cementation',
      description: 'Once your custom crown is ready, we remove the temporary one, verify the fit, shade, and bite of the permanent crown, and then securely bond it for a seamless and lasting result.'
    }
  ];

  const whoIsCandidate = [
    'Individuals with severely decayed teeth.',
    'Those with large fillings that compromise tooth integrity.',
    'Patients with cracked, fractured, or broken teeth.',
    'People needing to restore a tooth after root canal therapy.',
    'Anyone looking to improve the cosmetic appearance of a misshapen or discolored tooth.',
    'Patients requiring a restoration over a dental implant.'
  ];

  const typesOfCrowns = [
    {
      name: 'Porcelain/Ceramic Crowns',
      desc: 'Offer the most natural tooth color match and are excellent for front teeth. Highly biocompatible.'
    },
    {
      name: 'Zirconia Crowns',
      desc: 'Extremely strong and durable, suitable for back teeth due to their ability to withstand heavy biting forces. Good aesthetics.'
    },
    {
      name: 'E-MAX Crowns (Lithium Disilicate)',
      desc: 'Known for their excellent aesthetics and strength, a popular choice for both front and back teeth.'
    },
    {
        name: 'Porcelain-Fused-to-Metal (PFM)',
        desc: 'Combine the strength of metal with the aesthetics of porcelain. A time-tested option.',
    }
  ];

  const caringTips = [
    'Maintain excellent oral hygiene: Brush twice daily and floss once a day, paying special attention to the gum line around the crown.',
    'Attend regular dental check-ups and cleanings every six months.',
    'Avoid biting on very hard objects, ice, or using your crowned teeth as tools.',
    'If you clench or grind your teeth, discuss a nightguard with your dentist to protect your crown (and natural teeth).',
  ];
  
  const faqs = [
    {
      question: 'How long do dental crowns last?',
      answer: 'With proper care and regular dental check-ups, dental crowns can last for many years, often 10-15 years or even longer. The lifespan depends on factors like oral hygiene, the material of the crown, and biting habits.'
    },
    {
      question: 'Is getting a dental crown painful?',
      answer: 'The procedure is typically not painful. Local anesthesia is used to numb the tooth and surrounding area during preparation. You might experience some mild sensitivity or discomfort for a few days after, which can be managed with over-the-counter pain relievers.'
    },
    {
      question: 'How many visits does it take to get a dental crown?',
      answer: 'Traditionally, getting a dental crown requires two visits: one for preparation and impression, and a second for placing the permanent crown. Some practices offer same-day crowns using CEREC technology, which completes the process in a single visit.'
    },
    {
        question: 'Can a crowned tooth still get a cavity?',
        answer: 'While the crown itself cannot decay, the underlying tooth at the margin (where the crown meets the tooth) can still be susceptible to decay if not cared for properly. Good oral hygiene is crucial.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Dental Crowns | Expert Restoration | Pacifica Dental</title>
        <meta name="description" content="Restore your smile with custom dental crowns. We offer durable, natural-looking crowns to repair damaged teeth. Learn about types, process, and care." />
        <meta name="keywords" content="dental crowns, tooth caps, porcelain crowns, zirconia crowns, E-MAX crowns, restorative dentistry, cosmetic dentist, [Your City] dental crowns" />
      </Helmet>
      
      <Box 
        sx={{ 
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e9eef3 100%)', // Slightly updated gradient
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"} // Slightly larger for impact
              component="h1"
              textAlign="center"
              gutterBottom
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: 'primary.main' // Use primary color for main title
              }}
            >
              Revitalize Your Smile with Premium Dental Crowns
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{ mb: 4, color: 'text.secondary', fontWeight: 500 }}
            >
              Restoring Strength, Function, and Natural Beauty to Your Teeth
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 4, fontSize: '1.15rem', lineHeight: 1.8, color: 'text.primary' }} // Slightly larger font
            >
              At Pacifica Dental, we understand the impact a damaged or unsightly tooth can have on your confidence and oral health. Dental crowns, also known as caps, are a cornerstone of restorative dentistry. These custom-designed restorations encase the entire visible portion of a tooth, providing unparalleled strength, protection, and a remarkably natural appearance. Whether you need to repair a broken tooth, protect a weak one, or enhance your smile's aesthetics, our dental crowns offer a durable and beautiful solution.
            </Typography>
            <Divider sx={{ my: 4 }} />

            {/* Benefits Section */}
            <Grid container spacing={isMobile ? 3 : 5} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3} // Slightly more elevation
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    backgroundColor: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark',
                    color: 'white',
                    borderRadius: 2 // Rounded corners
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2}}>
                    Key Benefits of Our Dental Crowns
                  </Typography>
                  <List dense>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index} sx={{ py: 0.5}}>
                        <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5}}>
                          <CheckCircleOutlineIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
                        </ListItemIcon>
                        <ListItemText primary={benefit} primaryTypographyProps={{fontSize: '1.05rem'}} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Process Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom color="primary.main" sx={{ fontWeight: 600, mb: 2}}>
                    Your Journey to a Restored Smile: The Crown Process
                  </Typography>
                  {process.map((step, index) => (
                    <Box key={index} sx={{ mb: index === process.length - 1 ? 0 : 2.5 }}> {/* More spacing */}
                      <Typography variant="h6" color="primary.dark" fontWeight="bold" sx={{fontSize: '1.1rem'}}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.95rem', lineHeight: 1.6}}>
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 5 }} /> 

            {/* Who is a Good Candidate? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Are Dental Crowns Right for You?
              </Typography>
              <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e3f2fd' : '#2c3e50' }}> {/* Light blue background */}
                <Typography variant="body1" paragraph sx={{ mb: 2, fontSize: '1.1rem', color: 'text.secondary' }}>
                  Dental crowns are a versatile solution for various dental issues. You might be an ideal candidate if you experience:
                </Typography>
                <Grid container spacing={1}>
                    {whoIsCandidate.map((item, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                            <List dense sx={{py:0}}>
                                <ListItem sx={{py:0.2}}>
                                    <ListItemIcon sx={{minWidth: 'auto', marginRight: 1, color: 'primary.main'}}>
                                        <HealingIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={item} primaryTypographyProps={{fontSize: '1rem'}}/>
                                </ListItem>
                            </List>
                        </Grid>
                    ))}
                </Grid>
              </Paper>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Types of Crowns Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Material Options for Your Custom Crown
                </Typography>
                <Grid container spacing={3}>
                    {typesOfCrowns.map((type, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Paper elevation={2} sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
                                <Box sx={{display: 'flex', alignItems: 'center', mb:1}}>
                                    <StarIcon sx={{ color: 'secondary.main', mr: 1 }} />
                                    <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize:'1.1rem', color: 'secondary.dark'}}>
                                        {type.name}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{flexGrow: 1, fontSize: '0.95rem'}}>
                                    {type.desc}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Caring for Your Crown Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Long-Lasting Care for Your Dental Crown
                </Typography>
                 <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e8f5e9' : '#1b5e20' }}> {/* Light green background */}
                    <List>
                        {caringTips.map((tip, index) => (
                            <ListItem key={index}>
                                <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, color: 'primary.dark'}}>
                                    <SpaIcon />
                                </ListItemIcon>
                                <ListItemText primary={tip} primaryTypographyProps={{fontSize: '1rem'}}/>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Box>
            
            <Divider sx={{ my: 5 }} />


            {/* FAQ Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Frequently Asked Questions About Dental Crowns
              </Typography>
              {faqs.map((faq, index) => (
                <Accordion key={index} elevation={2} sx={{ mb: 1.5, borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary"/>}
                    aria-controls={`faq-panel${index}-content`}
                    id={`faq-panel${index}-header`}
                    sx={{ '& .MuiAccordionSummary-content': { fontWeight: 'medium', fontSize: '1.1rem' } }}
                  >
                    {faq.question}
                  </AccordionSummary>
                  <AccordionDetails sx={{backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#333' }}>
                    <Typography color="text.secondary" sx={{lineHeight: 1.7, fontSize: '1rem'}}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Why Choose Us Section (Enhanced) */}
            <Box sx={{ mt: 6, p:3, background: theme.palette.mode === 'light' ? 'rgba(0, 123, 255, 0.05)' : 'rgba(100,100,100,0.1)', borderRadius:2 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb:3 }}>
                Experience the Difference at Pacifica Dental
              </Typography>
              <Grid container spacing={3}>
                  {[
                      { title: "Artistry & Precision", text: "Our crowns are crafted with an artist's eye and a clinician's precision, ensuring a perfect match to your natural teeth.", icon: <StarIcon color="primary" /> },
                      { title: "Advanced Technology", text: "We utilize state-of-the-art digital imaging and materials for diagnostics, planning, and creation of your crowns.", icon: <CheckCircleOutlineIcon color="primary" /> },
                      { title: "Patient-Centered Care", text: "Your comfort and satisfaction are our top priorities. We listen to your needs and tailor treatment accordingly.", icon: <SpaIcon color="primary" /> },
                      { title: "Lasting Results", text: "We focus on providing durable crowns that not only look great but are built to last, protecting your oral health for years.", icon: <HealingIcon color="primary" /> }
                  ].map(item => (
                      <Grid item xs={12} sm={6} key={item.title}>
                          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
                              <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, marginTop: 0.5}}>
                                  {item.icon}
                              </ListItemIcon>
                              <Box>
                                  <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize: '1.1rem'}}>{item.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                              </Box>
                          </Box>
                      </Grid>
                  ))}
              </Grid>
            </Box>


            {/* Call to Action */}
            <Box sx={{ mt: 6, py: 4, textAlign: 'center', background: theme.palette.primary.main, color: 'white', borderRadius: 2 }}>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{fontWeight: 600}}>
                    Ready to Restore Your Confident Smile?
                </Typography>
                <Typography variant="body1" sx={{mb:3, opacity: 0.9}}>
                    Let us help you achieve the healthy, beautiful smile you deserve with our expertly crafted dental crowns.
                </Typography>
              <Button
                onClick={() => window.location.href = BOOKING_URL}
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 5, 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  }
                }}
              >
                Schedule Your Crown Consultation
              </Button>
              {/* Optional Secondary CTA */}
              <Typography variant="body2" sx={{mt: 2, opacity: 0.8}}>
                Or call us at <Link to="tel:(408) 869-0998" style={{color: 'white', fontWeight: 'bold'}}>(408) 869-0998</Link> for immediate assistance.
              </Typography>
            </Box>

          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default DentalCrowns;